import React from 'react'
import {
  Dropdown,
  IDropdownOption,
  Stack,
  TextField,
  MessageBar,
  MessageBarType,
  Text,
  Icon,
  getTheme,
} from 'office-ui-fabric-react'
import {
  OutstreamPlacementIntegrationType,
  OutstreamPlacementDocumentation,
} from '../../Api/outstreamPlacements_v2'
import {
  FixedPositionForm,
  INVALID_AD_FRAME_OPTIONS,
} from './StandardizedPlacementIntegrationForm/FixedPositionForm'
import { R2B2PlusForm } from './StandardizedPlacementIntegrationForm/R2B2PlusForm'
import { R2B2ReplacementForm } from './StandardizedPlacementIntegrationForm/R2B2ReplacementForm'
import { R2B2FloatingForm } from './StandardizedPlacementIntegrationForm/R2B2FloatingForm'
import { MinimalImaForm } from './StandardizedPlacementIntegrationForm/MinimalImaForm'
import { MultiPositionWithFloatingForm } from './StandardizedPlacementIntegrationForm/MultiPositionWithFloatingForm'
import {
  InReadToR2B2Form,
  UNLISTED_R2B2_STICKY,
} from './StandardizedPlacementIntegrationForm/InReadToR2B2Form'

interface Props {
  canAccessR2B2IntegrationTypes: boolean
  isAdmin: boolean
  placementId?: string
  gap: number
  disabled: boolean
  errorCode: string
  integration?: string
  integrationType: OutstreamPlacementIntegrationType
  integrationTypeConfig: any
  documentation?: OutstreamPlacementDocumentation
  onChange: (args: {
    integrationType: OutstreamPlacementIntegrationType
    config: any
  }) => void
}

export function OutstreamPlacementIntegrationForm({
  canAccessR2B2IntegrationTypes,
  isAdmin,
  placementId,
  gap,
  disabled,
  errorCode,
  integration,
  integrationType,
  integrationTypeConfig,
  documentation,
  onChange,
}: Props) {
  return (
    <Stack gap={gap}>
      {placementId ? (
        <>
          {' '}
          <div
            style={{
              flexGrow: 1,
              maxWidth: `calc(50% - ${gap / 2}px)`,
            }}
          >
            <TextField
              label={'Placement ID'}
              value={placementId}
              autoComplete={'off'}
              readOnly={true}
              disabled={disabled}
            />
          </div>
        </>
      ) : null}
      <Dropdown
        label="Insertion Rule"
        disabled={disabled}
        selectedKey={
          !canAccessR2B2IntegrationTypes &&
          [
            OutstreamPlacementIntegrationType.R2B2_PLUS,
            OutstreamPlacementIntegrationType.R2B2_FLOATING,
            OutstreamPlacementIntegrationType.R2B2_ONE,
            OutstreamPlacementIntegrationType.IN_READ_TO_R2B2,
          ].includes(integrationType)
            ? OutstreamPlacementIntegrationType.CUSTOM
            : integrationType
        }
        options={
          [
            { key: OutstreamPlacementIntegrationType.CUSTOM, text: 'Custom' },
            {
              key: OutstreamPlacementIntegrationType.FIXED_POSITION,
              text: 'Fixed Position',
            },
            {
              key: OutstreamPlacementIntegrationType.R2B2_REPLACEMENT,
              text: 'R2B2 Replacement',
            },
            {
              key: OutstreamPlacementIntegrationType.MULTI_POSITION_WITH_FLOATING,
              text: 'In-read to floating Multiformat',
            },
            canAccessR2B2IntegrationTypes
              ? {
                  key: OutstreamPlacementIntegrationType.IN_READ_TO_R2B2,
                  text: 'In-read to R2B2 Sticky',
                }
              : undefined,
            canAccessR2B2IntegrationTypes
              ? {
                  key: OutstreamPlacementIntegrationType.R2B2_PLUS,
                  text: 'R2B2+',
                }
              : undefined,
            canAccessR2B2IntegrationTypes
              ? {
                  key: OutstreamPlacementIntegrationType.R2B2_FLOATING,
                  text: 'R2B2 Floating',
                }
              : undefined,
            canAccessR2B2IntegrationTypes
              ? {
                  key: OutstreamPlacementIntegrationType.MINIMAL_IMA,
                  text: 'Minimal IMA',
                }
              : undefined,
            canAccessR2B2IntegrationTypes
              ? {
                  key: OutstreamPlacementIntegrationType.R2B2_ONE,
                  text: 'R2B2 One',
                }
              : undefined,
          ].filter(Boolean) as IDropdownOption[]
        }
        onChange={(
          _event: React.FormEvent<HTMLDivElement>,
          item: IDropdownOption | undefined
        ): void => {
          if (!item) {
            return
          }
          const newIntegrationType =
            !canAccessR2B2IntegrationTypes &&
            [
              OutstreamPlacementIntegrationType.R2B2_PLUS,
              OutstreamPlacementIntegrationType.R2B2_FLOATING,
              OutstreamPlacementIntegrationType.R2B2_ONE,
              OutstreamPlacementIntegrationType.IN_READ_TO_R2B2,
            ].includes(integrationType)
              ? integrationType
              : (item.key as OutstreamPlacementIntegrationType)
          onChange({ integrationType: newIntegrationType, config: {} })
        }}
      />
      {integrationType === OutstreamPlacementIntegrationType.CUSTOM &&
      documentation ? (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={true}>
          <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="small">{documentation.text}</Text>
            {documentation.aiGenerated ? (
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 4 }}
              >
                <Icon
                  iconName="Robot"
                  styles={{
                    root: {
                      fontSize: 12,
                      color: getTheme().palette.neutralSecondary,
                    },
                  }}
                />
                <Text
                  variant="xSmall"
                  styles={{
                    root: { color: getTheme().palette.neutralSecondary },
                  }}
                >
                  AI Generated
                </Text>
              </Stack>
            ) : null}
          </Stack>
        </MessageBar>
      ) : null}
      {integrationType === OutstreamPlacementIntegrationType.FIXED_POSITION ? (
        <FixedPositionForm
          disabled={disabled}
          errorCode={errorCode}
          isAdmin={isAdmin}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
      {integrationType === OutstreamPlacementIntegrationType.R2B2_PLUS ? (
        <R2B2PlusForm
          disabled={disabled}
          errorCode={errorCode}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
      {integrationType ===
      OutstreamPlacementIntegrationType.R2B2_REPLACEMENT ? (
        <R2B2ReplacementForm
          disabled={disabled}
          errorCode={errorCode}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
      {integrationType === OutstreamPlacementIntegrationType.R2B2_FLOATING ? (
        <R2B2FloatingForm
          disabled={disabled}
          errorCode={errorCode}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
      {integrationType === OutstreamPlacementIntegrationType.MINIMAL_IMA ? (
        <MinimalImaForm
          disabled={disabled}
          errorCode={errorCode}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
      {integrationType ===
      OutstreamPlacementIntegrationType.MULTI_POSITION_WITH_FLOATING ? (
        <MultiPositionWithFloatingForm
          disabled={disabled}
          errorCode={errorCode}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
      {integrationType === OutstreamPlacementIntegrationType.IN_READ_TO_R2B2 ? (
        <InReadToR2B2Form
          integration={integration}
          disabled={disabled}
          errorCode={errorCode}
          isAdmin={isAdmin}
          integrationTypeConfig={integrationTypeConfig}
          onIntegrationTypeConfigChange={(config: any) => {
            onChange({
              integrationType,
              config,
            })
          }}
        />
      ) : null}
    </Stack>
  )
}

export function getErrorCode({
  integrationType,
  config,
}: {
  integrationType: OutstreamPlacementIntegrationType
  config: any
}): string {
  switch (integrationType) {
    case OutstreamPlacementIntegrationType.FIXED_POSITION:
      if (config[INVALID_AD_FRAME_OPTIONS]) {
        return 'invalidAdFrameOptions'
      }
      try {
        const selector = config.selector.trim()
        if (!selector.length) {
          throw new Error()
        }
        document.querySelector(selector)
      } catch (_error) {
        return 'invalidSelector'
      }
      const hasWidth = config.width != null
      const hasHeight = config.height != null
      if (hasWidth !== hasHeight) {
        return 'invalidWidthHeight'
      }
      return ''
    case OutstreamPlacementIntegrationType.MINIMAL_IMA:
      try {
        const selector = config.selector.trim()
        if (!selector.length) {
          throw new Error()
        }
        document.querySelector(selector)
      } catch (_error) {
        return 'invalidSelector'
      }
      return ''
    case OutstreamPlacementIntegrationType.R2B2_REPLACEMENT:
      try {
        const code = config.code.trim()
        if (
          !code.length ||
          !code.includes('script') ||
          !code.includes('src=')
        ) {
          throw new Error()
        }
        // check for valid & complete HTML code
        const doc = document.createElement('div')
        doc.innerHTML = code
        if (doc.innerHTML !== code) {
          throw new Error()
        }
      } catch (_error) {
        return 'invalidCode'
      }

      if (typeof config.selector === 'string') {
        const selector = config.selector.trim()
        if (selector) {
          try {
            document.querySelector(selector)
          } catch (_error) {
            return 'invalidSelector'
          }
        }
      }
      return ''
    case OutstreamPlacementIntegrationType.R2B2_FLOATING: {
      const invalidWidth = config.maxWidth != null && config.maxWidth <= 0
      const invalidHeight = config.maxHeight != null && config.maxHeight <= 0
      if (invalidWidth || invalidHeight) {
        return 'invalidWidthHeight'
      }
      return ''
    }
    case OutstreamPlacementIntegrationType.MULTI_POSITION_WITH_FLOATING: {
      try {
        const selector = config.selector.trim().replace(/:sparse$/, '')
        if (!selector.length) {
          throw new Error()
        }
        document.querySelector(selector)
      } catch (_error) {
        return 'invalidSelector'
      }
      const invalidWidth = config.maxWidth != null && config.maxWidth <= 0
      const invalidHeight = config.maxHeight != null && config.maxHeight <= 0
      if (invalidWidth || invalidHeight) {
        return 'invalidWidthHeight'
      }
      return ''
    }
    case OutstreamPlacementIntegrationType.IN_READ_TO_R2B2: {
      if (config[INVALID_AD_FRAME_OPTIONS]) {
        return 'invalidAdFrameOptions'
      }
      try {
        const selector = (config.insertAfterSelector || '').trim()
        if (!selector.length) {
          throw new Error()
        }
        document.querySelector(selector)
      } catch (_error) {
        return 'invalidSelector'
      }
      if (config[UNLISTED_R2B2_STICKY]) {
        return 'r2b2StickyNotOnDomain'
      }
      return ''
    }
    default:
      return ''
  }
}
